import React, { useState } from "react";
import { FaBook, FaExternalLinkAlt, FaEdit, FaTrash } from "react-icons/fa";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import api from "@/services/api";
import placeholder from "@/assets/learn/placeholder.png";
import EditResourceModal from "../modals/EditResourceModal";

export default function BookCard({ resource, onUpdate }) {
  const user = useSelector((state) => state.Auth.user);
  const [isEditing, setIsEditing] = useState(false);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this resource?")) return;
    
    try {
      const { ok } = await api.remove(`/workshop/${resource._id || resource.id}`);
      if (ok) {
        toast.success("Deleted successfully");
        onUpdate();
      } else {
        toast.error("Failed to delete");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error deleting resource");
    }
  };

  const CardContent = (
    <div className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-gray-300 transition-all duration-300 flex flex-col h-full overflow-hidden transform hover:-translate-y-1 relative">
      <div className="relative h-48 overflow-hidden bg-gray-50">
        {resource.image ? (
          <img 
            src={resource.image} 
            alt={resource.title} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.target.onerror = null; 
              e.target.src = placeholder;
              e.target.classList.add("p-8", "object-contain", "opacity-50"); 
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-blue-100 text-blue-700 opacity-20">
            <div className="scale-[3]"><FaBook /></div>
          </div>
        )}
        
        <div className="absolute top-3 left-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold shadow-sm backdrop-blur-md bg-white/90 text-gray-700">
            <FaBook />
            Book
          </span>
        </div>

        {isAdmin && (
          <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsEditing(true);
              }}
              className="p-2 bg-white/90 hover:bg-white text-gray-600 hover:text-blue-600 rounded-lg shadow-sm backdrop-blur-sm transition-colors">
              <FaEdit className="w-3 h-3" />
            </button>
            <button
              onClick={handleDelete}
              className="p-2 bg-white/90 hover:bg-white text-gray-600 hover:text-red-600 rounded-lg shadow-sm backdrop-blur-sm transition-colors">
              <FaTrash className="w-3 h-3" />
            </button>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </div>

      <div className="p-5 flex flex-col flex-grow">
        <h3 className="font-bold text-gray-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors text-lg">
          {resource.title}
        </h3>
        
        {resource.author && (
          <p className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide">{resource.author}</p>
        )}

        <p className="text-sm text-gray-600 mb-4 line-clamp-3 flex-grow leading-relaxed">
          {resource.description || "No description available."}
        </p>

        {Array.isArray(resource.tags) && resource.tags.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1">
            {resource.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-md font-medium">
                {tag}
              </span>
            ))}
            {resource.tags.length > 3 && (
              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-md">
                +{resource.tags.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="pt-4 mt-auto border-t border-gray-100 flex items-center justify-between text-xs font-medium text-gray-500">
          <span>{resource.updatedAt ? new Date(resource.updatedAt).toLocaleDateString() : "Recently added"}</span>
          <span className="group-hover:translate-x-1 transition-transform duration-200 text-primary-600 flex items-center gap-1">
            View Book
            <FaExternalLinkAlt className="w-3 h-3" />
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <a href={resource.url} target="_blank" rel="noopener noreferrer" className="block h-full">
        {CardContent}
      </a>
      
      {isEditing && (
        <EditResourceModal 
          resource={resource} 
          onClose={() => setIsEditing(false)} 
          onUpdated={() => {
            setIsEditing(false);
            onUpdate();
          }} 
        />
      )}
    </>
  );
}
